/**
 * Which devices a tester must have before they can sign up for an application.
 *
 * Stored on the application as `testerRequirement` (RidgeWill/platform#56).
 * Auth enforces it when a tester card is sent (its lib/tester-requirements.ts
 * holds the same derivation and wording); this copy drives the editor's
 * sentence and its preview, so they must agree.
 *
 * ── The shape: groups joined by AND, platforms in a group joined by OR ──
 *
 * Jon asked for AND/OR logic over the platforms. Two levels cover every case
 * he described without turning into a query language:
 *
 *   Keyodo        (Android phone OR iPhone OR ...)            one group
 *   A Mac app     (macOS)                                     one group
 *   A companion   (any phone) AND (Windows OR macOS)
 *
 * A group can also be "all of", for an app that needs both an iPhone and an
 * Android phone at once (rare, but it is a tick box, not a new shape).
 *
 * ── Phones and tablets are separate (Jon, 2026-10-23) ───────────────────
 *
 * A platform names the kind of device as well as its system: `android-phone`,
 * `android-tablet`, `iphone`, `ipad`. A rule stored before the split can still
 * hold `android` or `ios`, which an Android phone or tablet (an iPhone or iPad)
 * meets; the editor turns them into the new values in an any-of group, where
 * that means the same thing.
 *
 * ── An app that has never been edited still has a rule ──────────────────
 *
 * `defaultRequirement` derives one from the platforms: every native platform
 * in one OR group, because that is where testing happens; Web on its own
 * accepts any device. Web is not counted beside native platforms, so a PC
 * alone does not qualify for Keyodo, which also has a web front end.
 */
import { legacyExpansion, platformLabel } from './platforms.js';

export type RequirementGroup = { mode: 'any' | 'all'; platforms: string[] };
export type Requirement = { groups: RequirementGroup[] };

export function defaultRequirement(platforms: string[]): Requirement {
  const native = platforms.filter((p) => p !== 'web');
  if (native.length) return { groups: [{ mode: 'any', platforms: native }] };
  return platforms.includes('web') ? { groups: [{ mode: 'any', platforms: ['web'] }] } : { groups: [] };
}

/**
 * A stored rule in today's values. In an any-of group `android` is exactly
 * "Android phone or Android tablet", so it is replaced; in an all-of group it
 * is not (that would demand both), so it stays and reads "Android: phone or
 * tablet".
 */
export function upgradeRequirement(r: Requirement): Requirement {
  return {
    groups: r.groups.map((g) =>
      g.mode === 'all'
        ? g
        : { ...g, platforms: [...new Set(g.platforms.flatMap((p) => legacyExpansion(p) ?? [p]))] },
    ),
  };
}

/** The platforms a device counts for: its own, and the pre-split one it meets. */
export function platformsOf(device: { type: string; os: string }): string[] {
  const { type, os } = device;
  if (os === 'android') return type === 'tablet' ? ['android-tablet', 'android'] : type === 'phone' ? ['android-phone', 'android'] : [];
  if (os === 'ios' && type === 'phone') return ['iphone', 'ios'];
  if (os === 'ipados' && type === 'tablet') return ['ipad', 'ios'];
  if (os === 'windows' && type === 'computer') return ['windows'];
  if (os === 'macos' && type === 'computer') return ['macos'];
  return [];
}

const DEVICE_NOUN: Record<string, string> = {
  'android-phone': 'an Android phone',
  'android-tablet': 'an Android tablet',
  iphone: 'an iPhone',
  ipad: 'an iPad',
  android: 'an Android phone or tablet',
  ios: 'an iPhone or iPad',
  windows: 'a Windows PC',
  macos: 'a Mac',
  web: 'any device with a browser',
};

/**
 * "an Android phone or an Android tablet" says the same as the older "an
 * Android phone or tablet", so an any-of group holding both says it that way.
 */
function nounsFor(g: RequirementGroup): string[] {
  let ps = g.platforms;
  if (g.mode === 'any') {
    for (const [legacy, pair] of [['android', ['android-phone', 'android-tablet']], ['ios', ['iphone', 'ipad']]] as const) {
      if (pair.every((p) => ps.includes(p))) {
        const at = Math.min(...pair.map((p) => ps.indexOf(p)));
        ps = ps.filter((p) => !(pair as readonly string[]).includes(p));
        ps.splice(at, 0, legacy);
      }
    }
  }
  return ps.map((p) => DEVICE_NOUN[p] ?? platformLabel(p));
}

function groupText(g: RequirementGroup): string {
  const nouns = nounsFor(g);
  if (nouns.length <= 1) return nouns[0] ?? 'nothing yet';
  const last = nouns[nouns.length - 1];
  // A comma before the last when a noun has its own "or" in it, so
  // "an Android phone or tablet, or an iPhone or iPad" still parses.
  const comma = nouns.length > 2 || nouns.some((n) => n.includes(' or ')) ? ',' : '';
  return `${nouns.slice(0, -1).join(', ')}${comma} ${g.mode === 'all' ? 'and' : 'or'} ${last}`;
}

/** The rule in a sentence, as ridgewill.com will say it to a tester. */
export function describeRequirement(r: Requirement): string {
  const parts = r.groups.filter((g) => g.platforms.length).map(groupText);
  if (!parts.length) return 'Anyone can sign up. No device is required.';
  return `A tester needs ${parts.join(', and also ')}.`;
}

/** Whether devices that count for these platforms (see `platformsOf`) meet the rule. */
export function meetsRequirement(r: Requirement, has: string[]): boolean {
  const ok = (p: string) => (p === 'web' ? has.length > 0 : has.includes(p));
  return r.groups.every((g) =>
    g.platforms.length === 0 || (g.mode === 'all' ? g.platforms.every(ok) : g.platforms.some(ok)),
  );
}

/** Platforms a requirement names that the app is not marked as running on. */
export function strayPlatforms(r: Requirement, platforms: string[]): string[] {
  return [...new Set(r.groups.flatMap((g) => g.platforms))].filter(
    (p) => !platforms.includes(p) && !(legacyExpansion(p) ?? []).some((x) => platforms.includes(x)),
  );
}
